import { useRef, useCallback, useEffect } from 'react';
import { registerPendingEdit } from '@/lib/email-builder/pending-edits';

interface UseDebouncedUpdateOptions {
  delay?: number;
  onUpdate: (value: string) => void;
}

/**
 * Hook for debouncing updates to prevent excessive API calls
 * 
 * Useful for live editing scenarios where you want to save changes
 * after the user stops typing for a specified delay.
 * 
 * @param options Configuration for debounced updates
 * @returns A function to trigger debounced updates
 * 
 * @example
 * ```tsx
 * const debouncedUpdate = useDebouncedUpdate({
 *   delay: 500,
 *   onUpdate: (value) => saveToServer(value)
 * });
 * 
 * // Call this on every change
 * debouncedUpdate('new text value');
 * ```
 */
export function useDebouncedUpdate({ delay = 500, onUpdate }: UseDebouncedUpdateOptions) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestValueRef = useRef<string>('');
  // Always call the latest onUpdate (it closes over the current block config)
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  // Sends the pending value now, if there is one
  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      onUpdateRef.current(latestValueRef.current);
    }
  }, []);

  // Flush on save/checkpoint (registry) and on unmount: dropping the timer here would lose
  // whatever was typed in the last `delay` ms before navigating away or deselecting the block.
  useEffect(() => {
    const unregister = registerPendingEdit(flush);
    return () => {
      flush();
      unregister();
    };
  }, [flush]);

  const debouncedUpdate = useCallback(
    (value: string) => {
      latestValueRef.current = value;

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        onUpdateRef.current(latestValueRef.current);
      }, delay);
    },
    [delay]
  );

  // Immediate update (blur, Ctrl+S): sends only if an edit is pending. Sending unconditionally
  // would push the initial empty value and wipe the block when nothing was typed.
  return { debouncedUpdate, immediateUpdate: flush };
}
